/**
 * Traduction FR → EN du contenu du site.
 *
 * `phrases` couvre les libellés exacts de l'interface (navigation, titres,
 * boutons, messages). `glossary` sert de repli mot à mot pour les phrases
 * rédigées qui ne figurent pas dans le dictionnaire.
 */
export const phrases: Record<string, string> = {
  // Navigation principale
  Ville: 'City',
  Services: 'Services',
  'Loisir et culture': 'Recreation and culture',
  'Communiquer avec nous': 'Contact us',
  Actualités: 'News',
  'Avis aux citoyens et avis publics': 'Citizen notices and public notices',
  'Assemblées du conseil municipal': 'City council meetings',
  'Conseil municipal et élus': 'City council and elected officials',
  'Grands projets et chantiers': 'Major projects and worksites',
  'Découvrir Brossard': 'Discover Brossard',
  Emplois: 'Jobs',
  'Collecte des matières résiduelles': 'Waste collection',
  'Taxes, eau et évaluation': 'Taxes, water and assessment',
  'Permis et règlements': 'Permits and by-laws',
  'Urbanisme et demandes': 'Urban planning and applications',
  Animaux: 'Animals',
  'Sécurité publique': 'Public safety',
  'Réseau routier et stationnement': 'Roads and parking',
  'Activités et inscriptions': 'Activities and registration',
  'Complexe aquatique de Brossard': 'Brossard Aquatic Complex',
  'Bibliothèque de Brossard': 'Brossard Library',
  'Camps de jour': 'Day camps',
  'Spectacles et billetterie': 'Shows and tickets',
  'Réservation de salles et plateaux sportifs': 'Room and sports facility booking',
  Organismes: 'Community organizations',
  'Nous joindre': 'Contact us',
  'Requêtes et signalements': 'Requests and reports',
  'Participation citoyenne': 'Citizen participation',
  Infolettre: 'Newsletter',
  'Magazine Ma Ville': 'Ma Ville magazine',

  // En-tête et accès rapides
  Plan: 'Map',
  'Ma carte': 'My card',
  'Mon espace': 'My space',
  'Mon profil': 'My profile',
  'Mes notifications': 'My notifications',
  'Mes collectes': 'My collections',
  'Mes demandes': 'My requests',
  'Mes loisirs': 'My activities',
  'Mes animaux': 'My pets',
  'Ma carte numérique': 'My digital card',
  'Mon espace citoyen': 'My citizen space',
  'Votre espace citoyen': 'Your citizen space',
  Accueil: 'Home',
  Requête: 'Request',
  Notifications: 'Notifications',
  'Inscriptions et réservations': 'Registration and bookings',
  Collectes: 'Collections',
  'Médailles animaux': 'Pet tags',
  'Demande de permis': 'Permit application',
  'Avis aux citoyens': 'Citizen notices',
  'Rechercher sur le site': 'Search the site',
  'Ouvrir le menu': 'Open menu',
  'Fermer le menu': 'Close menu',
  'Navigation principale': 'Main navigation',
  'Navigation mobile': 'Mobile navigation',
  'Quitter mon espace': 'Leave my space',
  'Se déconnecter': 'Sign out',
  'Se connecter': 'Sign in',
  Français: 'French',
  Anglais: 'English',

  // Hero
  'Championnats du Monde Route UCI 2026': '2026 UCI Road World Championships',
  'Les Mondiaux passent par Brossard': 'The World Championships come to Brossard',
  'Du 20 au 27 septembre 2026': 'September 20 to 27, 2026',
  'Le parcours traverse la ville. Entraves de circulation, accès aux quartiers et sites pour les spectateurs : préparez votre semaine.':
    'The route crosses the city. Traffic disruptions, neighbourhood access and spectator sites: plan your week.',
  'Voir le parcours et les entraves': 'See the route and disruptions',
  'Services en ligne': 'Online services',
  'Brossard dans votre poche avec bciti+': 'Brossard in your pocket with bciti+',
  'Disponible maintenant': 'Available now',
  'Requêtes, collectes, alertes, inscriptions aux activités et réservation de plateaux : tous vos services municipaux dans une seule application.':
    'Requests, collections, alerts, activity registration and facility booking: all your city services in a single app.',
  "Télécharger l'application": 'Download the app',
  "La programmation d'automne 2026 est en ligne": 'The fall 2026 program is online',
  'Inscriptions ouvertes depuis le 25 août': 'Registration open since August 25',
  'Activités sportives, cours de musique, ateliers artistiques et programmation pour les 50 ans et plus.':
    'Sports activities, music lessons, art workshops and programming for ages 50 and over.',
  'Voir la programmation': 'See the program',
  'Demander à ATNA…': 'Ask ATNA…',
  'Demander à ATNA': 'Ask ATNA',
  'Ma prochaine collecte': 'My next collection',
  'Signaler un bris': 'Report a problem',
  'Inscrire mon enfant': 'Register my child',
  'ATNA connaît les services de Brossard': 'ATNA knows Brossard’s services',
  'Assistant ATNA': 'ATNA assistant',
  'Services les plus demandés': 'Most requested services',

  // ATNA — conversation
  'Signaler un problème': 'Report a problem',
  'Inscription aux activités': 'Activity registration',
  Conversation: 'Conversation',
  Messages: 'Messages',
  'Fermer la conversation': 'Close conversation',
  'Nouvelle conversation': 'New conversation',
  'Répondre à ATNA, ou préciser votre demande…': 'Reply to ATNA, or add details…',
  'Autre chose à signaler ?': 'Anything else to report?',
  'ATNA peut se tromper. Vérifiez le résumé avant d’envoyer votre demande.':
    'ATNA can make mistakes. Review the summary before sending your request.',
  'ATNA peut se tromper. Vérifiez l’horaire avant de confirmer une inscription.':
    'ATNA can make mistakes. Check the schedule before confirming a registration.',
  'Où se trouve le problème ?': 'Where is the problem?',
  'Choisissez l’endroit en un clic.': 'Choose the location in one click.',
  'Utiliser ma position actuelle': 'Use my current location',
  'Localisation en cours…': 'Locating…',
  Principale: 'Primary',
  'Quelle activité voulez-vous ?': 'Which activity would you like?',
  Inscription: 'Registration',
  Recherche: 'Search',
  Recommandé: 'Recommended',
  Choisir: 'Choose',
  'Voir mes activités': 'See my activities',
  'Ajouter au calendrier': 'Add to calendar',
  'Inscrire à autre chose': 'Register for something else',
  'Inscrire à une autre activité ?': 'Register for another activity?',
  'Nommez une activité, ex. « cours de piscine »…': 'Name an activity, e.g. “swimming lessons”…',
  'Aucune place trouvée pour cette activité.': 'No spots found for this activity.',
  'Nommez une autre activité et je regarde de nouveau.': 'Name another activity and I’ll look again.',
  'Recherche assistée par ATNA': 'ATNA-assisted search',
  'Inscrire mon enfant, trouver une activité…': 'Register my child, find an activity…',
  'Propulsé par ATNA': 'Powered by ATNA',
  'Trouver une activité': 'Find an activity',
  'Réserver un espace': 'Book a space',
  'Louer de l’équipement': 'Rent equipment',
  Natation: 'Swimming',
  Soccer: 'Soccer',
  Patinage: 'Skating',
  'Arts plastiques': 'Visual arts',
  Musique: 'Music',

  // Collectes
  'Collecte en cours': 'Collection in progress',
  'Vos prochaines collectes': 'Your upcoming collections',
  'Collecte suivante': 'Next collection',
  'Arrivée estimée': 'Estimated arrival',
  'Distance actuelle': 'Current distance',
  'Votre adresse': 'Your address',
  'Sur place': 'On site',
  'Arrive bientôt': 'Arriving soon',
  'En route': 'On the way',
  Rappels: 'Reminders',
  Calendrier: 'Calendar',
  Carte: 'Map',
  Recyclage: 'Recycling',
  Ordures: 'Garbage',
  'Matières organiques': 'Organic waste',
  'Résidus verts': 'Yard waste',
  'Raccourcis collectes': 'Collection shortcuts',
  'Calendrier des collectes': 'Collection calendar',

  // Demandes
  Détails: 'Details',
  Description: 'Description',
  'Description de la requête': 'Request description',
  Emplacement: 'Location',
  Dates: 'Dates',
  'Fichiers joints': 'Attached files',
  'Aucun fichier joint.': 'No attached files.',
  'Ajouter un fichier': 'Add a file',
  Historique: 'History',
  'Créée le': 'Created on',
  Résolue: 'Resolved',
  Actualiser: 'Refresh',
  'Travaux publics': 'Public works',
  'Signalements publics': 'Public reports',
  'Je confirme': 'I confirm',
  Nouveau: 'New',
  'Équipe en route': 'Crew on the way',
  Réglé: 'Resolved',
  'En direct': 'Live',
  Tous: 'All',
  Danger: 'Hazard',
  Voirie: 'Roads',
  Éclairage: 'Lighting',
  Arbres: 'Trees',
  'Réponse attendue': 'Response required',
  'Document à remplir et à retourner': 'Form to complete and return',
  'Joindre le formulaire rempli': 'Attach the completed form',
  'La Ville attend un document de votre part': 'The City is waiting for a document from you',

  // Profil et compte
  'Informations personnelles': 'Personal information',
  'Mes adresses': 'My addresses',
  'Gestion du compte': 'Account management',
  'Préférences de communication': 'Communication preferences',
  'Connexion requise': 'Sign-in required',
  'J’ai déjà un compte': 'I already have an account',
  'Créer mon compte': 'Create my account',
  'Courriel ou numéro de mobile': 'Email or mobile number',
  'Recevoir mon code': 'Get my code',
  'Entrez votre code': 'Enter your code',
  Confirmer: 'Confirm',
  'Connexion réussie': 'Signed in',
  'Compte créé': 'Account created',
  'Ouverture de vos services…': 'Opening your services…',
  'Ce module est réservé aux citoyens connectés.': 'This section is reserved for signed-in residents.',
  'Étape précédente': 'Previous step',
  Fermer: 'Close',

  // Plan
  'Plan de la ville': 'City map',
  'Lieux d’intérêt': 'Points of interest',
  'Services municipaux': 'City services',
  'Sports et loisirs': 'Sports and recreation',
  Culture: 'Culture',
  Parcs: 'Parks',
  Écocentres: 'Eco-centres',
  Ouvert: 'Open',
  Fermé: 'Closed',
  Itinéraire: 'Directions',
}

/** Repli mot à mot pour le contenu rédigé. */
export const glossary: Record<string, string> = {
  et: 'and',
  ou: 'or',
  le: 'the',
  la: 'the',
  les: 'the',
  un: 'a',
  une: 'a',
  des: 'the',
  du: 'of the',
  de: 'of',
  au: 'at the',
  aux: 'at the',
  votre: 'your',
  vos: 'your',
  vous: 'you',
  mon: 'my',
  ma: 'my',
  mes: 'my',
  nos: 'our',
  notre: 'our',
  ce: 'this',
  cette: 'this',
  pour: 'for',
  avec: 'with',
  sans: 'without',
  sur: 'on',
  dans: 'in',
  par: 'by',
  plus: 'more',
  tout: 'all',
  tous: 'all',
  toute: 'all',
  toutes: 'all',
  ville: 'city',
  citoyen: 'resident',
  citoyens: 'residents',
  citoyenne: 'resident',
  service: 'service',
  services: 'services',
  demande: 'request',
  demandes: 'requests',
  requête: 'request',
  requêtes: 'requests',
  collecte: 'collection',
  collectes: 'collections',
  adresse: 'address',
  adresses: 'addresses',
  carte: 'card',
  activité: 'activity',
  activités: 'activities',
  inscription: 'registration',
  inscriptions: 'registration',
  enfant: 'child',
  famille: 'family',
  animal: 'pet',
  animaux: 'pets',
  profil: 'profile',
  compte: 'account',
  courriel: 'email',
  téléphone: 'phone',
  message: 'message',
  messages: 'messages',
  photo: 'photo',
  document: 'document',
  documents: 'documents',
  place: 'spot',
  places: 'spots',
  heure: 'hour',
  heures: 'hours',
  jour: 'day',
  jours: 'days',
  semaine: 'week',
  mois: 'month',
  aujourd: 'today',
  demain: 'tomorrow',
  prochaine: 'next',
  prochain: 'next',
  nouvelle: 'new',
  nouveau: 'new',
  voir: 'see',
  ajouter: 'add',
  envoyer: 'send',
  modifier: 'edit',
  supprimer: 'delete',
  confirmer: 'confirm',
  annuler: 'cancel',
  fermer: 'close',
  ouvrir: 'open',
  chercher: 'search',
  trouver: 'find',
  signaler: 'report',
  inscrire: 'register',
  gérer: 'manage',
  suivre: 'track',
  lundi: 'Monday',
  mardi: 'Tuesday',
  mercredi: 'Wednesday',
  jeudi: 'Thursday',
  vendredi: 'Friday',
  samedi: 'Saturday',
  dimanche: 'Sunday',
  janvier: 'January',
  février: 'February',
  mars: 'March',
  avril: 'April',
  mai: 'May',
  juin: 'June',
  juillet: 'July',
  août: 'August',
  septembre: 'September',
  octobre: 'October',
  novembre: 'November',
  décembre: 'December',
}
